<template>
    <div class="todolist" :class="[viewType]">
        <!-- Daily Card List -->
        <perfect-scrollbar style="height: 100%; width: 100%">
            <div class="daily-card-list">
                <div class="daily-card" v-for="data in todoDatas" :key="data[0]">
                    <div class="daily-card-title">
                        <h2>{{ getDateString(data[0]).dayFullString }}</h2>
                        <h3 :class="[isTodayForTitle(data[0]) ? 'today' : '']">
                            {{ getDateString(data[0]).monthFullString }}
                            {{ getDateString(data[0]).dayShortNumber }}
                        </h3>
                    </div>
                    <div class="draggable-wrapper">
                        <perfect-scrollbar>
                            <draggable
                                class="list-group"
                                group="todos"
                                handle=".handle"
                                :animation="100"
                                @change="evt => onMoveTask(evt, data[0])"
                                @start="isDragging = true"
                                @end="isDragging = false"
                                :list="data[1]"
                                v-bind="dragOptions"
                            >
                                <transition-group class="transition-group" type="transition" name="flip-list">
                                    <div
                                        v-for="task in data[1]"
                                        :key="task.id"
                                        class="task"
                                        :class="[task.isDone ? 'done' : '']"
                                        @mouseenter="showSubButtons"
                                        @mouseover="showSubButtons"
                                        @mouseleave="hideSubButtons"
                                    >
                                        <div class="handle">
                                            <i class="fa fa-align-justify"></i>
                                        </div>
                                        <iu-checkbox
                                            size="small"
                                            v-model="task.isDone"
                                            @change="onToggleDone(task.id, task.isDone)"
                                        ></iu-checkbox>
                                        <div class="text">
                                            <iu-input
                                                :ref="'field-' + task.id"
                                                maxlength="50"
                                                placeholder="To-do"
                                                v-model="task.title"
                                                @focus="focusText(task.title)"
                                                @blur="handleBlurEvent(data[0], task.id, task.title)"
                                                @keydown.enter.native="handleEnterEvent(data[0], task.id)"
                                                @keydown.up.native="moveFocus(task.id, 'prev')"
                                                @keydown.down.native="moveFocus(task.id, 'next')"
                                                @keydown.tab.native.prevent="handleMoveFocus(task.id, $event)"
                                                @input="inputText"
                                                @keyup.delete.native="event => handleDeleteKey(event, data[0], task.id)"
                                            ></iu-input>
                                        </div>
                                        <div class="contents" @click="onShowEditContents(data[0], task.id)">
                                            <i class="fa fa-file"></i>
                                        </div>
                                        <div class="trash" @click="onDeleteTask(data[0], task.id)">
                                            <i class="fa fa-trash"></i>
                                        </div>
                                    </div>
                                    <div v-if="addNewTask && addNewTaskDate === data[0]" class="task" key="newtodo">
                                        <div class="handle"></div>
                                        <iu-checkbox size="small"></iu-checkbox>
                                        <div class="text">
                                            <iu-input
                                                v-model="newTodoTitle"
                                                ref="field-newtodo"
                                                maxlength="50"
                                                placeholder="To-do"
                                                @blur="handleAddNewTask(data[0], newTodoTitle)"
                                                @keydown.enter.native="handleAddNewTask(data[0], newTodoTitle)"
                                                @keydown.up.native="moveFocus(data[1][data[1].length - 1].id)"
                                                @keyup.delete.native="handleDeleteKeyForNewTask"
                                            ></iu-input>
                                        </div>
                                        <div class="contents">
                                            <i class="fa fa-file"></i>
                                        </div>
                                        <div class="trash" @click="onDeleteTask(data[0], task.id)">
                                            <i class="fa fa-trash"></i>
                                        </div>
                                    </div>
                                </transition-group>

                                <div
                                    slot="footer"
                                    key="footer"
                                    v-if="!addNewTask"
                                    :class="addTaskButtonClass"
                                    :style="addTaskButtonTopStyle(data[0])"
                                >
                                    <iu-button @click="handleOpenNewTask(data[0])" type="text">
                                        <i class="fa fa-plus"></i>
                                    </iu-button>
                                </div>
                            </draggable>
                        </perfect-scrollbar>
                    </div>
                </div>
            </div>
        </perfect-scrollbar>
    </div>
</template>

<script>
import draggable from 'vuedraggable';
import utils from '@/utils';

export default {
    components: {
        draggable,
    },
    data: function() {
        return {
            dragOptions: {
                animation: 0,
                group: 'description',
                disabled: false,
                ghostClass: 'ghost',
            },
            prevTitle: '',
            addNewTask: false,
            addNewTaskDate: '',
            addNewTaskIsEmpty: false,
            newTodoTitle: '',
            isDragging: false,
        };
    },
    methods: {
        addTaskButtonTopStyle(date) {
            const lengthOfTasks = [...this.todoDatas.find(x => x[0] === date)[1]].length;
            let top = lengthOfTasks * 35; // Why 35?

            return {
                top: `${top}px`,
            };
        },
        getDateString(date) {
            const dateString = utils.date.getDetailTextByDate(
                `${date.toString().substring(0, 4)}-${date.toString().substring(4, 6)}-${date
                    .toString()
                    .substring(6, 8)}`
            );
            return dateString;
        },
        isTodayForTitle(date) {
            return utils.date.getToday().isSame(utils.date.parseFormatToDate(date), 'day');
        },
        showSubButtons(evt) {
            if (!this.isDragging) {
                evt.currentTarget.classList.add('showSubButton');
            }
        },
        hideSubButtons(evt) {
            if (!this.isDragging) {
                evt.currentTarget.classList.remove('showSubButton');
            }
        },
        inputText(text) {
            if (text !== '') {
                this.willRemoveFocusedTask = false;
            }
        },
        focusText(title) {
            this.prevTitle = title;
            this.willRemoveFocusedTask = !title || title === '';
        },
        handleDeleteKey(evt, date, id) {
            if (!this.willRemoveFocusedTask && evt.target.value === '') {
                this.willRemoveFocusedTask = true;
            } else if (this.willRemoveFocusedTask) {
                this.onDeleteTask(date, id);
                if (evt.key === 'Backspace') {
                    this.moveFocus(id, 'prev');
                } else if (evt.key === 'Delete') {
                    this.moveFocus(id, 'next');
                }
            }
        },
        handleMoveFocus(id, e) {
            if (e.shiftKey) {
                this.moveFocus(id, 'prev');
            } else {
                this.moveFocus(id, 'next');
            }
        },
        moveFocus(id, direction) {
            if (!id) return;

            let targetTaskId = id;

            if (direction) {
                const sortedAllTodos = this.$store.state.todos.todos;
                let targetIndex = sortedAllTodos.findIndex(x => x.id === id);
                let targetTaskIndex = direction === 'prev' ? targetIndex - 1 : targetIndex + 1;

                // Validate next text field
                if (direction === 'prev' && targetIndex <= 0) return;
                if (direction === 'next' && targetIndex + 1 >= sortedAllTodos.length) return;
                targetTaskId = sortedAllTodos[targetTaskIndex].id;
            }

            this.$refs['field-' + targetTaskId][0].$el.getElementsByTagName('input')[0].focus();
        },
        handleEnterEvent(date, taskId) {
            const lastTask = [...this.todoDatas.find(x => x[0] === date)[1]].pop();
            const isLast = lastTask.id === taskId;

            if (isLast) {
                this.handleOpenNewTask(date);
            } else {
                this.moveFocus(taskId, 'next');
            }
        },
        handleAddNewTask(date, title) {
            this.handleCloseNewTask();

            if (!title) return;

            this.onAddTask(date, title);
        },
        handleBlurEvent(date, id, title) {
            if (this.prevTitle === title) return;

            this.onSaveText(date, id);
        },
        handleOpenNewTask(date) {
            this.newTodoTitle = '';
            this.addNewTask = true;
            this.addNewTaskDate = date;
            this.$nextTick(function() {
                this.$refs['field-newtodo'][0].$el.getElementsByTagName('input')[0].focus();
            });
        },
        handleDeleteKeyForNewTask() {
            if (this.newTodoTitle) {
                this.addNewTaskIsEmpty = false;
                return;
            } else if (!this.newTodoTitle && !this.addNewTaskIsEmpty) {
                this.addNewTaskIsEmpty = true;
                return;
            }

            this.handleCloseNewTask();
        },
        handleCloseNewTask() {
            this.newTodoTitle = '';
            this.addNewTask = false;
            this.addNewDate = false;
            this.addNewTaskIsEmpty = false;
        },
    },
    computed: {
        addTaskButtonClass: function() {
            return {
                'add-task-btn-wrapper': true,
                hidden: this.isDragging,
            };
        },
    },
    props: {
        viewType: { type: String },
        todoDatas: { type: Array },
        onDeleteTask: { type: Function },
        onSaveText: { type: Function },
        onAddTask: { type: Function },
        onMoveTask: { type: Function },
        onShowEditContents: { type: Function },
        onToggleDone: { type: Function },
    },
};
</script>

<style lang="scss" scope>
.draggable-wrapper {
    .ps__rail-x {
        display: none;
    }
}

.add-task-btn-wrapper {
    position: absolute;
    margin: auto;
    color: rgb(133, 133, 133);
    border: 0px solid #dcdfe6;
    top: 0;
    left: calc(50% - 20px);
    width: 40px;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }

        to {
            opacity: 1;
        }
    }

    @keyframes fadeOut {
        from {
            opacity: 1;
        }

        to {
            opacity: 0;
        }
    }

    animation: 0.2s ease 0s 1 normal both running fadeIn;

    &.hidden {
        animation: 0.2s ease 0s 1 normal both running fadeOut;
    }

    button {
        color: #565656;

        &:hover {
            color: #666;
        }
    }
}
</style>
